<template>
  <div id="inventoryPowerUser">
    <Modal name="edit-item"
      :width="600"
      :actions="[
        {name: $t('back'), action: 'close'},
        {name: $t('save'), action: editItem, primary: true}
      ]">
      <ItemPowerForm v-if="inventory && itemFormData" v-model="itemFormData" :inventory-id="inventory.id" />
    </Modal>

    <div class="container">

      <div style="display: flex; justify-content: space-between">
        <div class="group">
          <label>{{ $t('inventory_power_user.visibility') }}</label>
          <div v-for="f of fields">
            <input :id="`show_field_${f.name}`" type="checkbox" v-model="f.show">
            <label :for="`show_field_${f.name}`">{{ $t(f.transKey || f.name) }}</label>
          </div>
        </div>
        <div class="buttons">
          <button class="btn btn-primary" @click="save">{{ $t('save') }}</button>
          <button class="btn btn-secondary" @click="csvDownload">{{ $t('csv_download') }}</button>
        </div>
      </div>

      <div class="actions-bar">
        <button class="btn btn-primary" @click="addItemModal">{{ $t('inventory_power_user.add_item') }}</button>
        <ExternalInventoryUpdater v-if="inventory" :inventory="inventory.id"/>
      </div>

      <div style="overflow-x: auto; overflow-y: hidden;">
        <table v-if="rows.length" class="power-table">
          <!-- COLUMNS NAME -->
          <tr>
            <th style="width: 40px"><!-- ACTION --></th>
            <th v-for="f of shownFields" :style="f.style || {}">{{ $t(f.transKey || f.name) }}</th>
          </tr>

          <!-- COLUMNS FILTER -->
          <tr class="filters">
            <td><!-- ACTION --></td>
            <td v-for="f of shownFields">
              <select v-if="f.filterChoice" v-model="localFilters[f.filterKey]">
                <option value=""></option>
                <option :value="c" v-for="c of f.filterChoice">{{c}}</option>
              </select>
              <input v-else type="text" v-model="localFilters[f.filterKey]">
            </td>
          </tr>

          <!-- DATA -->
          <tr v-for="row of filteredRows" :key="`row_${row.id}`" :class="{'is-dirty': isRowDirty(row)}">
            <td>
              <!-- ACTION -->
              <button @click="editItemModal(row)" class="btn btn-secondary">
                <svgicon src="/icon/open.svg" class="icon"/>
              </button>
              <button v-if="isRowNew(row)" @click="deleteRow(row)" class="btn btn-secondary">
                <svgicon src="/icon/circle-remove.svg" class="icon"/>
              </button>
            </td>

            <td v-for="field of shownFields" :key="`${field.transKey || field.name}_${row.id}`">
              <!-- SHOW -->
              <template v-if="!field.edit">{{ rowFieldToString(row, field) }}</template>

              <!-- EDIT -->
              <template v-else>
                <!-- WHEN COLUMN IS A PRICE-->
                <template v-if="field.format === 'price'">
                  <!-- PRICE INPUT -->
                  <input
                      type="number" step="0.01" placeholder="00.00"
                      v-if="getScheme(row, field.method)"
                      v-model.number="getScheme(row, field.method).unitPrice"
                  >
                  <!-- MISSING PRICE -->
                  <template v-else>{{ 'none_m' }}</template>
                </template>

                <!-- EXTERNAL ID INPUT -->
                <template v-else-if="field.name === 'externalId'">
                  <ExternalIDInput v-if="inventory" v-model="row.externalId" :inventory="inventory.id" :sync-item="row"
                                   :prefill-initial-data="row.externalItemDataOnLoad || {}" :forceSelect="true"/>
                </template>

                <!-- EMBEDDED -->
                <input v-else-if="field.language" type="text" v-model="row[field.name][field.language]">

                <!-- DEFAULT INPUT -->
                <input v-else type="text" v-model="row[field.name]" :maxlength="field.maxLength || undefined">
              </template>
            </td>
          </tr>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
  import { Core } from '@/core/Core';
  import ExternalInventoryUpdater from "@/components/inventory/ExternalInventoryUpdater";
  import ExternalIDInput from "@/components/inventory/ExternalIDInput";
  import ItemPowerForm from "@/components/inventory/ItemPowerForm";
  import Vue from "vue";
  import {debouncedComputedProperty, filterRows} from "@/vueUtils";

  export default {
    name: "InventoryPowerUser",
    components: {ExternalIDInput, ExternalInventoryUpdater, ItemPowerForm},

    data() {
      return {
        inventory: null,
        initialHashes: {},
        externalItems: {},
        itemFormData: null,
        lastAddedType: null,
        localFilters: {},

        rows: [],
        fields: [{
          name: 'id',
          show: false,
          edit: false,
          style: {width: '60px'},
        }, {
          name: 'type',
          show: true,
          edit: false,
          style: {width: '80px'},
          filterChoice: [
            'item',
            'variant',
            'modifier',
            'combo',
            'supplement'
          ]
        }, {
          name: 'externalId',
          transKey: 'external_id',
          show: true,
          edit: true,
          style: {width: '300px'},
        }, {
          name: 'priceSchemes',
          show: false,
          edit: true,
          format: 'price',
          initialValue: () => this.inventoryPriceMethods.map(method => ({method, unitPrice: 0})),
          style: {width: '110px'},
        }, {
          name: 'name',
          show: true,
          edit: true,
          initialValue: () => this.inventoryLanguages.reduce((obj, language) => { obj[language] = ''; return obj; }, {}),
          format: 'trans',
          style: {width: '200px'},
        }, {
          name: 'note',
          show: true,
          edit: true,
          maxLength: 16,
          style: {width: '200px'},
        }, {
          name: 'parentName',
          show: true,
          edit: false,
          format: 'trans',
          style: {width: '200px'},
        }, {
          name: 'categoryNames',
          show: false,
          edit: false,
          format: 'trans',
          style: {width: '200px'},
        }]
      }
    },

    computed: {
      localFiltersDebounced: debouncedComputedProperty(555, {}),

      filteredRows() {
        return filterRows(this.rows, this.localFiltersDebounced);
      },

      editableFields() {
        return this.fields.filter(f => f.edit)
      },

      inventoryLanguages() {
        let languages = this.inventory ? this.inventory.languages : []
        if (languages.length === 0) {
          languages.push('en')
        }
        return languages
      },

      inventoryPriceMethods() {
        return this.inventory.allSchemeMethods;
      },

      shownFields() {
        let shown = [];
        for (let f of this.fields) {
          if (f.show) {
            if (f.name === 'priceSchemes') {
              for (let method of this.inventoryPriceMethods) {
                let filterKey = `${f.name}.${method}`;
                Vue.set(this.localFilters, filterKey, '');
                shown.push({transKey: `deliveryMethod.${method}`, method, filterKey, ...f});
              }
            } else if (f.name === 'name') {
              for (let language of this.inventoryLanguages) {
                let filterKey = `${f.name}.${language}`;
                Vue.set(this.localFilters, filterKey, '');
                shown.push({transKey: `translation.${language}`, language, filterKey, ...f});
              }
            } else {
              let filterKey = f.name;
              Vue.set(this.localFilters, filterKey, '');
              f.filterKey = filterKey;
              shown.push(f);
            }
          }
        }
        return shown;
      }
    },

    async mounted() {
      this.inventory = await this.$store.getters.getContextInventory;
    },

    watch: {
      'inventory.id': {
        immediate: true,
        async handler(inventory) {
          this.rows = [];
          if (!inventory) {
            return;
          }
          await this.loadExternalItems(inventory);
          await this.loadOrderables(inventory);
        }
      },
      localFilters: {
        deep: true,
        handler(val) {
          this.localFiltersDebounced = JSON.parse(JSON.stringify(val))
        }
      },
    },

    methods: {
      getScheme(row, method) {
        return row.priceSchemes.find(s => s.method === method)
      },

      computeInitialHashes() {
        for(let row of this.rows) {
          this.initialHashes[row.id] = this.hashRow(row);
        }
      },

      hashCode(str) {
        let hash = 0;
        for (let i = 0, len = str.length; i < len; i++) {
          let chr = str.charCodeAt(i);
          hash = ((hash << 5) - hash) + chr;
          hash |= 0; // Convert to 32bit integer
        }
        return hash;
      },

      hashRow(row) {
        let str = this.editableFields.map(f => JSON.stringify(row[f.name])).join('&');
        return this.hashCode(str);
      },

      isRowDirty(row) {
        return this.hashRow(row) !== this.initialHashes[row.id]
      },

      isRowNew(row) {
        return typeof row.id === 'string' && row.id.includes('tmp');
      },

      deleteRow(row) {
        if (this.isRowNew(row)) {
          let index = this.rows.indexOf(row)
          if (index !== -1) {
            this.rows.splice(index, 1);
          }
        }
      },

      rowFieldToString(row, field) {
        if (field.format === 'trans') {
          return Array.isArray(row[field.name])
            ? row[field.name].map(f => this.$to(f)).join(', ')
            : this.$to(row[field.name]);

        } else if (field.format === 'price') {
          return this.getScheme(row, field.method).unitPrice;
        } else {
          return row[field.name];
        }
      },

      csvDownload() {
        let fileName = `inventory_${Date.now()}`;
        window._downloadAsCsv(
          fileName,
          [].concat([this.shownFields.map(f => this.$t(f.transKey || f.name))])
            .concat(this.filteredRows.map(r => this.shownFields.map(f => this.rowFieldToString(r, f))))
        );
      },

      addItemModal() {
        if (!this.itemFormData || !this.isRowNew(this.itemFormData)) {
          let item = {
            id: `tmp_${Math.floor(Math.random()*100000)}`,
            type: this.lastAddedType || 'item',
          }
          for (let f of this.editableFields) {
            item[f.name] = f.initialValue ? f.initialValue() : null
          }
          this.itemFormData = item
        }
        this.$modal.show('edit-item')
      },

      editItemModal(row) {
        this.itemFormData = JSON.parse(JSON.stringify(row));
        this.$modal.show('edit-item');
      },

      editItem() {
        this.$modal.hide('edit-item');
        if (this.itemFormData) {
          let itemData = this.itemFormData;
          let row = this.rows.find(r => r.id === itemData.id);
          if (row) {
            Object.assign(row, itemData)
          } else {
            this.rows.push(itemData);
            this.lastAddedType = itemData.type;
          }
          this.itemFormData = null;
        }
      },

      async loadExternalItems(inventory) {
        let externalItems = await Core.noThrowRequest("GET", `/api/inventory/${inventory}/external_items`);
        if (externalItems) {
          this.externalItems = {}
          for (let e of externalItems) {
            this.externalItems[e.id.toString()] = e;
          }
        }
      },

      async loadOrderables(inventory) {
        this.rows = [];
        let orderables = await Core.noThrowRequest('GET', `/api/inventory/${inventory}/orderables`, {
          fields: this.fields.map(f => f.name),
          noParent: true,
          admin: true
        });
        if (orderables) {
          for (let r of orderables) {
            if (typeof r.externalId === 'string' && r.externalId.trim()) {
              r.externalItemDataOnLoad = this.externalItems[r.externalId.trim()] || {name: ''}
            }
          }
          this.rows = orderables;
          this.computeInitialHashes();
        }
      },

      async save() {
        let fieldsToSave = this.editableFields.map(f => f.name);
        let payload = this.rows
          .filter(r => this.isRowDirty(r))
          .map(r => {
            let isNew = this.isRowNew(r);
            let item = {};
            if (isNew) {
              item.id = null;
              item.type = r.type || 'item';
            } else {
              item.id = r.id;
            }
            for (let name of fieldsToSave) {
              item[name] = r[name]
            }
            return item;
          });

        showSpinner()
        let response = await Core.noThrowRequest('PUT', `/api/inventory/${this.inventory.id}/item_mass_update`, { payload });
        hideSpinner()

        if (response) {
          let message = this.$t('inventory_power_user.save_success').replace('%NB%', payload.length);
          toast(message, 'success');

          let hasNewItems = !!payload.find(i => i.id === null);
          if (hasNewItems) {
            await this.loadOrderables(this.inventory.id)
          } else {
            this.computeInitialHashes();
            this.$forceUpdate();
          }
        }
      }
    }

  }
</script>

<style lang="scss" scoped>
  .power-table {
    width: 100%;
    table-layout: fixed;
    margin-top: 20px;

    .filters {
      input, select {
        padding: 0;
        height: 24px;
        width: 100%;
      }
    }

    td,
    th {
      padding: 4px 4px;
      text-align: left;

      &.money {
        font-family: Monospace, serif;
        text-align: right;
      }

      input {
        width: 100%;
      }

      button {
        padding: 4px;
      }
    }

    th {
      white-space: nowrap;
      background-color: #ddd;
    }

    tr:nth-child(odd) {
      background-color: rgba(0, 0, 0, .05);
    }

    tr.is-dirty {
      background-color: lightseagreen;
    }
  }

  .buttons {
    display: flex;
    flex-direction: column;

    button {
      margin-bottom: 10px;
    }
  }

  .actions-bar {
    display: flex;
    justify-content: center;
    > :not(:last-child) {
      margin-right: 10px;
    }
  }
</style>